import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { SERVICES } from "../data/site";

export default function ServiceDetailPage({ onQuoteClick }) {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;
  const Icon = service.icon;
  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];

  return (
    <div data-testid="service-detail-page">
      <Seo title={`${service.title} — IDEAL ENTERPRISES`} description={service.short} />

      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-brand-blue text-white overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-25" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
          <Link to="/services" data-testid="back-to-services" className="inline-flex items-center gap-2 text-white/70 hover:text-brand-orange text-sm">
            <ArrowLeft size={14} /> All services
          </Link>
          <div className="mt-6 flex items-start gap-6">
            <div className="w-16 h-16 grid place-items-center bg-brand-orange shrink-0">
              <Icon size={28} className="text-white" />
            </div>
            <div>
              <p className="overline text-brand-orange">Service Vertical / {(idx+1).toString().padStart(2,"0")}</p>
              <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl mt-3 leading-tight">
                {service.title}
              </h1>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="overline">Overview</p>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-brand-blue mt-4">
              {service.short}
            </h2>
            <p className="mt-6 text-slate-700 leading-relaxed">{service.long}</p>

            <div className="mt-10">
              <p className="overline mb-4">Scope &amp; Deliverables</p>
              <ul className="space-y-3">
                {service.deliverables.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-slate-700">
                    <CheckCircle2 size={20} className="text-brand-orange shrink-0 mt-0.5" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="bg-brand-blue text-white p-8 sticky top-28">
              <p className="overline text-brand-orange">Get an Estimate</p>
              <h3 className="font-heading font-bold text-2xl mt-3">
                Have a {service.title.toLowerCase()} scope?
              </h3>
              <p className="text-white/75 text-sm mt-3 leading-relaxed">
                Share your tentative length, pressure class, and location — we'll respond within one business day with a feasibility note.
              </p>
              <button
                onClick={() => onQuoteClick(service.title)}
                data-testid="service-detail-quote"
                className="mt-6 w-full bg-brand-orange text-white py-3.5 font-semibold btn-lift hover:bg-orange-600"
              >
                Request a Quote
              </button>
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-xs text-white/60 font-mono-tech tracking-wider">NEXT SERVICE</p>
                <Link to={`/services/${next.slug}`} data-testid="next-service-link" className="mt-2 inline-flex items-center gap-2 text-white hover:text-brand-orange">
                  {next.title} <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
